import React, { useState } from "react";
import { Button, Select, TextInput, MultiSelect } from "@/components";
import { Dialog } from "@/components/ui/dialog";
import {
  Trash2,
  ArrowLeft,
  Calendar,
  Image as ImageIcon,
  Video,
  Edit,
  User,
} from "lucide-react";
import type { 
  Agent, 
  AgentStatus, 
  CompanyAgentStatus,
  AgentGender,
  AgentAgeGroup,
  AgentBodyType,
  AgentEthnicity,
  AgentHairType,
  AgentAccessory,
  AgentDressing,
  AgentBeard,
  AgentAttributes,
} from "../interfaces";
import {
  getBodyTypeOptions,
  getBeardOptions,
  getHairTypeOptions,
  autoCorrectAttributes,
} from "../utils/attributeRules";

const STATUS_OPTIONS: { value: CompanyAgentStatus; label: string }[] = [
  { value: "ACTIVE" as CompanyAgentStatus, label: "Active" },
  { value: "REVIEW" as CompanyAgentStatus, label: "Review" },
  { value: "ARCHIVED" as CompanyAgentStatus, label: "Archived" },
];

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: "bg-green-500/10 text-green-600 dark:text-green-400",
  REVIEW: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  ARCHIVED: "bg-muted text-muted-foreground",
};

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non-binary', label: 'Non-Binary' },
];

const AGE_GROUP_OPTIONS = [
  { value: 'early 20s', label: 'Early 20s' },
  { value: 'mid 20s', label: 'Mid 20s' },
  { value: 'late 20s', label: 'Late 20s' },
  { value: 'early 30s', label: 'Early 30s' },
  { value: 'mid 30s', label: 'Mid 30s' },
  { value: 'late 30s', label: 'Late 30s' },
  { value: 'early 40s', label: 'Early 40s' },
  { value: 'mid 40s', label: 'Mid 40s' },
];

const ETHNICITY_OPTIONS = [
  { value: 'American', label: 'American' },
  { value: 'African', label: 'African' },
  { value: 'East Asian', label: 'East Asian' },
  { value: 'South Asian', label: 'South Asian' },
  { value: 'Middle Eastern', label: 'Middle Eastern' },
  { value: 'European', label: 'European' },
  { value: 'Latino', label: 'Latino' },
  { value: 'Caribbean', label: 'Caribbean' },
  { value: 'Mixed', label: 'Mixed' },
];

const ACCESSORIES_OPTIONS = [
  { value: 'glasses', label: 'Glasses' },
  { value: 'sunglasses', label: 'Sunglasses' },
  { value: 'beanie', label: 'Beanie' },
  { value: 'baseball_cap', label: 'Baseball Cap' },
  { value: 'fedora', label: 'Fedora' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'watch', label: 'Watch' },
  { value: 'earrings', label: 'Earrings' },
  { value: 'necklace', label: 'Necklace' },
  { value: 'scarf', label: 'Scarf' },
  { value: 'none', label: 'None' },
];

const DRESSING_OPTIONS = [
  { value: 'casual', label: 'Casual' },
  { value: 'business casual', label: 'Business Casual' },
  { value: 'formal', label: 'Formal' },
  { value: 'streetwear', label: 'Streetwear' },
  { value: 'sporty', label: 'Sporty' },
  { value: 'minimalist', label: 'Minimalist' },
  { value: 'creative', label: 'Creative' },
  { value: 'bohemian', label: 'Bohemian' },
  { value: 'vintage', label: 'Vintage' },
];

interface AgentDetailProps {
  agent: Agent;
  onDelete: (agentId: number) => void;
  onBack: () => void;
  onStatusChange?: (agent: Agent, status: AgentStatus) => void;
  onUpdate?: (agentId: number, name?: string, attributes?: Partial<AgentAttributes>) => void;
  isUpdatingStatus?: boolean;
  isUpdating?: boolean;
}

export const AgentDetail: React.FC<AgentDetailProps> = ({
  agent,
  onDelete,
  onBack,
  onStatusChange,
  onUpdate,
  isUpdatingStatus = false,
  isUpdating = false,
}) => {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    name: agent.name,
    attributes: { ...agent.attributes },
  });
  const [wasUpdating, setWasUpdating] = React.useState(false);

  // Close dialog when update completes successfully
  React.useEffect(() => {
    if (wasUpdating && !isUpdating) {
      setIsEditDialogOpen(false);
    }
    setWasUpdating(isUpdating);
  }, [isUpdating, wasUpdating]);

  const handleDelete = () => {
    onDelete(agent.id);
  };

  const handleEditSubmit = () => {
    if (!onUpdate || isUpdating) return;
    
    const nameChanged = editForm.name !== agent.name;
    const attributesChanged = JSON.stringify(editForm.attributes) !== JSON.stringify(agent.attributes);
    
    if (nameChanged || attributesChanged) {
      onUpdate(
        agent.id,
        nameChanged ? editForm.name : undefined,
        attributesChanged ? editForm.attributes : undefined
      );
      // Don't close dialog immediately - let the mutation handle it on success
    }
  };

  const handleOpenEdit = () => {
    setEditForm({
      name: agent.name,
      attributes: { ...agent.attributes },
    });
    setIsEditDialogOpen(true);
  };

  const statusStyle = STATUS_COLORS[agent.status] || STATUS_COLORS.ARCHIVED;

  return (
    <>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={onBack}
                title="Back to list"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold">{agent.name}</h1>
                <span
                  className={`px-2 py-1 rounded text-xs font-medium ${statusStyle}`}
                >
                  {agent.status}
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              {onUpdate && (
                <Button variant="outline" onClick={handleOpenEdit}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              )}
              {onStatusChange && (
                <Select
                  label="Status"
                  value={agent.status}
                  onChange={(val) => onStatusChange(agent, val as AgentStatus)}
                  options={STATUS_OPTIONS}
                  disabled={isUpdatingStatus}
                  fullWidth={false}
                />
              )}
              <Button variant="destructive" onClick={handleDelete}>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Created: {new Date(agent.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Updated: {new Date(agent.updatedAt).toLocaleDateString()}</span>
            </div>
            {agent.strategyIds && agent.strategyIds.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {agent.strategyIds.map((id) => (
                  <span
                    key={id}
                    className="px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium"
                  >
                    Plan #{id}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Attributes Section */}
        {agent.attributes && (
          <div className="bg-card border border-input rounded-lg p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5" />
              <h2 className="text-xl font-semibold">Agent Attributes</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Gender</p>
                <p className="font-medium capitalize">{agent.attributes.gender}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Age Group</p>
                <p className="font-medium capitalize">{agent.attributes.ageGroup}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Body Type</p>
                <p className="font-medium capitalize">{agent.attributes.bodyType}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Ethnicity</p>
                <p className="font-medium">{agent.attributes.ethnicity}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Hair Type</p>
                <p className="font-medium capitalize">{agent.attributes.hairType}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Accessories</p>
                <p className="font-medium capitalize">
                  {agent.attributes.accessories.join(", ")}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Dressing</p>
                <p className="font-medium capitalize">{agent.attributes.dressing}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Beard</p>
                <p className="font-medium capitalize">{agent.attributes.beard}</p>
              </div>
            </div>
          </div>
        )}

      {/* Loading State for Image Regeneration */}
      {isUpdating && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="h-5 w-5" />
            <h2 className="text-xl font-semibold">Images</h2>
          </div>
          <div className="bg-muted/50 rounded-lg p-12 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
            <p className="font-medium text-primary mb-2">Regenerating Images</p>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              We're generating new images based on the updated attributes. This typically takes 5-30 seconds.
            </p>
          </div>
        </div>
      )}

      {/* Images */}
      {!isUpdating && agent.images && agent.images.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Images ({agent.images.length})
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {agent.images.map((image, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg overflow-hidden bg-muted group cursor-pointer hover:ring-2 hover:ring-primary transition-all"
              >
                <img
                  src={image}
                  alt={`${agent.name} image ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  onClick={() => window.open(image, "_blank")}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {agent.videos && agent.videos.length > 0 && (
        <div className="bg-card border border-input rounded-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Video className="h-5 w-5" />
            <h2 className="text-xl font-semibold">
              Videos ({agent.videos.length})
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agent.videos.map((video, index) => (
              <div
                key={index}
                className="aspect-video rounded-lg overflow-hidden bg-muted"
              >
                {video.includes("youtube.com/embed") ||
                video.includes("youtu.be") ? (
                  <iframe
                    src={video}
                    title={`${agent.name} video ${index + 1}`}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={video} controls className="w-full h-full">
                    Your browser does not support the video tag.
                  </video>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state for media */}
      {(!agent.images || agent.images.length === 0) &&
        (!agent.videos || agent.videos.length === 0) && (
          <div className="bg-card border border-input rounded-lg p-12 text-center">
            <p className="text-muted-foreground">
              No images or videos for this agent.
            </p>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onOpenChange={(open) => !isUpdating && setIsEditDialogOpen(open)}
        title="Edit Agent"
        description={
          isUpdating
            ? "Updating agent and regenerating images..."
            : "Update the agent's name and attributes"
        }
      >
        <div className="space-y-4 pt-4">
          {/* Loading Indicator */}
          {isUpdating && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              <div className="text-sm">
                <p className="font-medium text-primary">Updating agent...</p>
                <p className="text-xs text-muted-foreground">
                  This may take a few moments as we regenerate images with the new attributes
                </p>
              </div>
            </div>
          )}

          <TextInput
            label="Agent Name"
            value={editForm.name}
            onChange={(e) =>
              setEditForm({ ...editForm, name: e.target.value })
            }
            placeholder="Agent name"
            disabled={isUpdating}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Gender"
              value={editForm.attributes.gender || ""}
              onChange={(val) => {
                const newGender = val as AgentGender;
                const corrections = autoCorrectAttributes({
                  gender: newGender,
                  bodyType: editForm.attributes.bodyType,
                  ethnicity: editForm.attributes.ethnicity,
                  hairType: editForm.attributes.hairType,
                  beard: editForm.attributes.beard,
                });
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    gender: newGender,
                    ...corrections,
                  },
                });
              }}
              options={GENDER_OPTIONS}
              placeholder="Select gender..."
              disabled={isUpdating}
            />

            <Select
              label="Age Group"
              value={editForm.attributes.ageGroup || ""}
              onChange={(val) =>
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    ageGroup: val as AgentAgeGroup,
                  },
                })
              }
              options={AGE_GROUP_OPTIONS}
              placeholder="Select age group..."
              disabled={isUpdating}
            />

            <Select
              label="Body Type"
              value={editForm.attributes.bodyType || ""}
              onChange={(val) =>
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    bodyType: val as AgentBodyType,
                  },
                })
              }
              options={getBodyTypeOptions(editForm.attributes.gender)}
              placeholder="Select body type..."
              disabled={isUpdating}
            />

            <Select
              label="Ethnicity"
              value={editForm.attributes.ethnicity || ""}
              onChange={(val) => {
                const newEthnicity = val as AgentEthnicity;
                const corrections = autoCorrectAttributes({
                  gender: editForm.attributes.gender,
                  bodyType: editForm.attributes.bodyType,
                  ethnicity: newEthnicity,
                  hairType: editForm.attributes.hairType,
                  beard: editForm.attributes.beard,
                });
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    ethnicity: newEthnicity,
                    ...corrections,
                  },
                });
              }}
              options={ETHNICITY_OPTIONS}
              placeholder="Select ethnicity..."
              disabled={isUpdating}
            />

            <Select
              label="Hair Type"
              value={editForm.attributes.hairType || ""}
              onChange={(val) =>
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    hairType: val as AgentHairType,
                  },
                })
              }
              options={getHairTypeOptions(editForm.attributes.ethnicity)}
              placeholder="Select hair type..."
              disabled={isUpdating}
            />

            <MultiSelect
              label="Accessories"
              value={(editForm.attributes.accessories || []) as string[]}
              onChange={(val) =>
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    accessories: val as AgentAccessory[],
                  },
                })
              }
              options={ACCESSORIES_OPTIONS}
              placeholder="Select accessories..."
            />

            <Select
              label="Dressing"
              value={editForm.attributes.dressing || ""}
              onChange={(val) =>
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    dressing: val as AgentDressing,
                  },
                })
              }
              options={DRESSING_OPTIONS}
              placeholder="Select dressing style..."
              disabled={isUpdating}
            />

            <Select
              label="Beard"
              value={editForm.attributes.beard || ""}
              onChange={(val) =>
                setEditForm({
                  ...editForm,
                  attributes: {
                    ...editForm.attributes,
                    beard: val as AgentBeard,
                  },
                })
              }
              options={getBeardOptions(editForm.attributes.gender)}
              placeholder="Select beard style..."
              disabled={isUpdating}
            />
          </div>

          {/* Info Message */}
          {!isUpdating && (
            <div className="bg-muted/50 border border-muted rounded-lg p-3 text-xs text-muted-foreground">
              💡 <strong>Note:</strong> Updating physical attributes (gender, body type, ethnicity, etc.) will automatically regenerate the agent's images to match the new appearance.
            </div>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleEditSubmit}
              disabled={isUpdating || !editForm.name.trim()}
            >
              {isUpdating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
